import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { withRouter, Route, Switch, Redirect } from 'react-router-dom';

import { checkAuthentication } from 'redux/modules/account';
import { setTabID } from 'redux/modules/tabID';

import withRoot from 'components/common/withRoot';
import PrivateRoute from 'components/common/PrivateRoute';
import NotificationsSnackbar from 'components/common/NotificationsSnackbar';

import Index from 'pages/home/Index';
import Charts from 'pages/charts/Charts';
import Patient from 'pages/charts/Patient';
import PatientCreate from 'pages/charts/PatientCreate';
import PatientPrint from 'pages/charts/PatientPrint';
import Inventory from 'pages/inventory/Inventory';
import Vendors from 'pages/inventory/Vendors';
import Categories from 'pages/inventory/Categories';
import Conditions from 'pages/inventory/Conditions';
import Warehouses from 'pages/inventory/Warehouses';
import BusinessGeneral from 'pages/settings/BusinessGeneral';
import BusinessFacilities from 'pages/settings/BusinessFacilities';
import BusinessFacilityCreate from 'pages/settings/BusinessFacilityCreate';
import BusinessFacilityEdit from 'pages/settings/BusinessFacilityEdit';
import BusinessChangeCredentials from 'pages/settings/BusinessChangeCredentials';
import BusinessClaimMDCredentials from 'pages/settings/BusinessClaimMDCredentials';
import BusinessTwilioCredentials from 'pages/settings/BusinessTwilioCredentials';
import Users from 'pages/settings/Users';
import UserCreateEdit from 'pages/settings/UserCreateEdit';
import MedicalSourcesReferrals from 'pages/settings/MedicalSourcesReferrals';
import MedicalSourcesSales from 'pages/settings/MedicalSourcesSales';
import MedicalSourcesPrescribers from 'pages/settings/MedicalSourcesPrescribers';
import MedicalSourcesNoteCategories from 'pages/settings/MedicalSourcesNoteCategories';
import DataPortabilityHCPCSCodes from 'pages/settings/DataPortabilityHCPCSCodes';
import DataPortabilityDiagnoses from 'pages/settings/DataPortabilityDiagnoses';
import DataPortabilityInsurances from 'pages/settings/DataPortabilityInsurances';
import DataPortability from 'pages/settings/DataPortability';
import DataPortabilityVariant from 'pages/settings/DataPortabilityVariant';
import Templates from 'pages/settings/Templates';
import TemplateCreateEdit from 'pages/settings/TemplateCreateEdit';
import TemplatePrint from 'pages/settings/TemplatePrint';
import Payment from 'pages/settings/Payment';
import PaymentPlanDetails from 'pages/settings/PaymentPlanDetails';
import PaymentBusiness from 'pages/settings/PaymentBusiness';
import PaymentContract from 'pages/settings/PaymentContract';
import PaymentSuccess from 'pages/settings/PaymentSuccess';
import PaymentCancel from 'pages/settings/PaymentCancel';
import System from 'pages/settings/System';
import Roles from 'pages/settings/Roles';
import RoleCreateEdit from 'pages/settings/RoleCreateEdit';
import Orders from 'pages/workflow/Orders';
import DeliverySchedule from 'pages/workflow/DeliverySchedule';
import DeliverySchedulePrint from 'pages/workflow/DeliverySchedulePrint';
import OrderCreatePatientAssign from 'pages/workflow/OrderCreatePatientAssign';
import OrderCreatePatientCreate from 'pages/workflow/OrderCreatePatientCreate';
import OrderCreateEdit from 'pages/workflow/OrderCreateEdit';
import OrderView from 'pages/workflow/OrderView';
import OrderPrint from 'pages/workflow/OrderPrint';
import ConnectedOrders from 'pages/workflow/ConnectedOrders';
import Error404 from 'pages/common/Error404';
import Versions from 'pages/common/Versions';
import Download from 'pages/common/Download';
import AccountLogin from 'pages/account/AccountLogin';
import AccountLoginConfirm from 'pages/account/AccountLoginConfirm';
import AccountResetPasswordEmail from 'pages/account/AccountResetPasswordEmail';
import AccountResetPassword from 'pages/account/AccountResetPassword';
import AccountSignUp from 'pages/account/AccountSignUp';
import AccountSignUpEmailConfirm from 'pages/account/AccountSignUpEmailConfirm';
import AccountSignUpPhoneConfirm from 'pages/account/AccountSignUpPhoneConfirm';
import AccountSignUpCompany from 'pages/account/AccountSignUpCompany';
import Profile from 'pages/account/Profile';
import ReportCreateEdit from 'pages/reports/ReportCreateEdit';
import Claims from 'pages/claims/Claims';
import DocumentPrint from 'pages/common/DocumentPrint';
import WorkspaceDocumentCategories from 'pages/settings/WorkspaceDocumentCategories';
import WorkspaceWorkflowStatuses from 'pages/settings/WorkspaceWorkflowStatuses';
import WorkspaceClaimStatuses from 'pages/settings/WorkspaceClaimStatuses';
import WorkspaceClaimAdjustmentCodes from 'pages/settings/WorkspaceClaimAdjustmentCodes';
import PrivacyPolicy from 'pages/common/PrivacyPolicy';
import TermsOfUse from 'pages/common/TermsOfUse';

class App extends Component {
  constructor(props) {
    super(props);

    this.state = {
      lastAuthenticationCheck: Date.now(),
    };

    this.onAuthenticationCheck = this.onAuthenticationCheck.bind(this);
  }

  componentDidMount() {
    this.props.actions.checkAuthentication();
    let tabID = sessionStorage.getItem('tabID');
    if (tabID === null) {
      const lastTabID = localStorage.getItem('lastTabID');
      tabID = lastTabID === null ? 1 : Number(lastTabID) + 1;
      localStorage.setItem('lastTabID', tabID);
      sessionStorage.setItem('tabID', tabID);
      this.props.actions.setTabID(tabID);
    }
    window.addEventListener('scroll', this.onAuthenticationCheck);
    window.addEventListener('click', this.onAuthenticationCheck);
  }

  componentWillUnmount() {
    window.removeEventListener('scroll', this.onAuthenticationCheck);
    window.removeEventListener('click', this.onAuthenticationCheck);
  }

  onAuthenticationCheck() {
    const authenticationCheckThrottleTime = 60000;
    const currentAuthenticationCheck = Date.now();
    if (currentAuthenticationCheck - this.state.lastAuthenticationCheck > authenticationCheckThrottleTime) {
      this.setState({ lastAuthenticationCheck: currentAuthenticationCheck });
      this.props.actions.checkAuthentication();
    }
  }

  render() {
    const { account, redirect } = this.props;
    if (account.authenticated === null) {
      return '';
    }
    return (
      <div className="App">
        <NotificationsSnackbar />
        <Switch>
          <Route
            exact
            path="/account"
            render={() => (
              <Redirect to="/account/login" />
            )}
          />
          <Route
            exact
            path="/account/login"
            render={(props) => {
              if (account.authenticated) {
                return <Redirect to="/" />;
              }
              return <AccountLogin {...props} />;
            }}
          />
          <Route exact path="/account/login" component={AccountLogin} />
          <Route exact path="/account/login-confirm" component={AccountLoginConfirm} />
          <Route exact path="/account/reset-password" component={AccountResetPasswordEmail} />
          <Route exact path="/account/sign-up" component={AccountSignUp} />
          <Route exact path="/check_email" component={AccountSignUpEmailConfirm} />
          <Route exact path="/account/sign-up-phone-confirm" component={AccountSignUpPhoneConfirm} />
          <Route exact path="/account/sign-up-company" component={AccountSignUpCompany} />
          <Route
            exact
            path="/reload_password_check"
            render={(props) => <AccountResetPassword variant="reset" {...props} />}
          />
          <Route
            exact
            path="/new_user_verify_check"
            render={(props) => <AccountResetPassword variant="create" {...props} />}      
          />
          <Route exact path="/privacy-policy" component={PrivacyPolicy} />
          <Route exact path="/terms-of-use" component={TermsOfUse} />
          <PrivateRoute exact path="/" component={Index} />
          <Route
            exact
            path="/home"
            render={() => (
              <Redirect to="/" />
            )}
          />
          <PrivateRoute exact path="/charts" component={Charts} />
          <PrivateRoute exact path="/charts/create" component={PatientCreate} />
          <PrivateRoute exact path="/charts/:id/:activeSection?" component={Patient} />
          <PrivateRoute
            exact
            path="/charts/:id/print/document/:document"
            component={PatientPrint}
            variant="document"
          />
          <PrivateRoute
            exact
            path="/charts/:id/print/template/:template"
            component={PatientPrint}
            variant="template"
          />
          <Route
            exact
            path="/inventory"
            render={() => (
              <Redirect to="/inventory/products" />
            )}
          />
          <PrivateRoute exact path="/inventory/products" component={Inventory} />
          <PrivateRoute exact path="/inventory/vendors" component={Vendors} />
          <PrivateRoute exact path="/inventory/categories" component={Categories} />
          <PrivateRoute exact path="/inventory/conditions" component={Conditions} />
          <PrivateRoute exact path="/inventory/warehouses" component={Warehouses} />
          <Route
            exact
            path="/settings"
            render={() => (
              <Redirect to="/settings/business" />
            )}
          />
          <Route
            exact
            path="/settings/business"
            render={() => (
              <Redirect to="/settings/business/general" />
            )}
          />
          <PrivateRoute exact path="/settings/business/general" component={BusinessGeneral} />
          <PrivateRoute exact path="/settings/business/facilities" component={BusinessFacilities} />
          <PrivateRoute
            exact
            path="/settings/business/facilities/create"
            component={BusinessFacilityCreate}
            Roles={['Administrator', 'Master Administrator']}
          />
          <PrivateRoute
            exact
            path="/settings/business/facilities/:id"
            component={BusinessFacilityEdit}
            Roles={['Administrator', 'Master Administrator']}
          />
          <PrivateRoute exact path="/settings/business/change-credentials" component={BusinessChangeCredentials} />
          <PrivateRoute
            exact
            path="/settings/business/claim-md-credentials" component={BusinessClaimMDCredentials}
            Roles={['Administrator', 'Master Administrator']}
          />
          <PrivateRoute
            exact
            path="/settings/business/twilio-credentials"
            component={BusinessTwilioCredentials}
            Roles={['Administrator', 'Master Administrator']}
          />
          <PrivateRoute
            exact
            path="/settings/users"
            Roles={['Administrator', 'Master Administrator']}
            component={Users}
          />
          <PrivateRoute
            exact
            path="/settings/users/create"
            Roles={['Administrator', 'Master Administrator']}
            variant="create"
            component={UserCreateEdit}
          />
          <PrivateRoute
            exact
            path="/settings/users/:id"
            Roles={['Administrator', 'Master Administrator']}
            variant="edit"
            component={UserCreateEdit}
          />
          <PrivateRoute
            exact
            path="/settings/roles"
            Roles={['Administrator', 'Master Administrator']}
            component={Roles}
          />
          <PrivateRoute
            exact
            path="/settings/roles/create"
            Roles={['Administrator', 'Master Administrator']}
            component={RoleCreateEdit}
            variant="create"
          />
          <PrivateRoute
            exact
            path="/settings/roles/edit/:id"
            Roles={['Administrator', 'Master Administrator']}
            component={RoleCreateEdit}
            variant="edit"
          />
          <PrivateRoute
            exact
            path="/settings/roles/copy"
            Roles={['Administrator', 'Master Administrator']}
            component={RoleCreateEdit}
            variant="copy"
          />
          <Route
            exact
            path="/settings/medical-sources"
            render={() => (
              <Redirect to="/settings/medical-sources/referrals" />
            )}
          />
          <PrivateRoute exact path="/settings/medical-sources/referrals" component={MedicalSourcesReferrals} />
          <PrivateRoute exact path="/settings/medical-sources/sales" component={MedicalSourcesSales} />
          <PrivateRoute exact path="/settings/medical-sources/prescribers" component={MedicalSourcesPrescribers} />
          <PrivateRoute exact path="/settings/medical-sources/hcpc" component={DataPortabilityHCPCSCodes} />
          <PrivateRoute exact path="/settings/medical-sources/diagnosis" component={DataPortabilityDiagnoses} />
          <PrivateRoute exact path="/settings/medical-sources/payers" component={DataPortabilityInsurances} />
          <PrivateRoute exact path="/settings/data-portability" component={DataPortability} />
          <PrivateRoute exact path="/settings/data-portability/:id" component={DataPortabilityVariant} />
          <PrivateRoute
            exact
            path="/settings/templates"
            form="templateCreate"
            component={Templates}
          />
          <PrivateRoute
            exact
            path="/settings/templates/create"
            variant="create"
            form="templateCreate"
            component={TemplateCreateEdit}
          />
          <PrivateRoute
            exact
            path="/settings/templates/create/print"
            component={TemplatePrint}
          />
          <PrivateRoute
            exact
            path="/settings/templates/:id"
            variant="edit"
            form="templateEdit"
            component={TemplateCreateEdit}
          />
          <PrivateRoute
            exact
            path="/settings/templates/:id/print"
            component={TemplatePrint}
          />
          <Route
            exact
            path="/settings/payment"
            render={() => (
              <Redirect to="/settings/payment/dashboard" />
            )}
          />
          <PrivateRoute exact path="/settings/payment/dashboard" component={Payment} />
          <PrivateRoute exact path="/settings/payment/plan-details" component={PaymentPlanDetails} />
          <PrivateRoute exact path="/settings/payment/business" component={PaymentBusiness} />
          <PrivateRoute exact path="/settings/payment/contract" component={PaymentContract} />
          <PrivateRoute exact path="/settings/payment/success" component={PaymentSuccess} />
          <Route
            exact
            path="/settings/payment/cancel"
            render={() => (
              <Redirect to="/settings/payment/dashboard" />
            )}
          />
          <PrivateRoute exact path="/settings/payment/cancel" component={PaymentCancel} />
          <PrivateRoute
            exact
            path="/settings/system"
            Roles={['Master Administrator']}
            component={System}
          />
          <Route
            exact
            path="/settings/workspace"
            render={() => (
              <Redirect to="/settings/workspace/note-categories" />
            )}
          />
          <PrivateRoute
            exact
            path="/settings/workspace/note-categories"
            Roles={['Administrator', 'Master Administrator']}
            component={MedicalSourcesNoteCategories}
          />
          <PrivateRoute
            exact
            path="/settings/workspace/document-categories"
            Roles={['Administrator', 'Master Administrator']}
            component={WorkspaceDocumentCategories}
          />
          <PrivateRoute
            exact
            path="/settings/workspace/workflow-statuses"
            Roles={['Administrator', 'Master Administrator']}
            component={WorkspaceWorkflowStatuses}
          />
          <PrivateRoute
            exact
            path="/settings/workspace/claim-statuses"
            Roles={['Administrator', 'Master Administrator']}
            component={WorkspaceClaimStatuses}
          />
          <PrivateRoute
            exact
            path="/settings/workspace/claim-adjustment-codes"
            Roles={['Administrator', 'Master Administrator']}
            component={WorkspaceClaimAdjustmentCodes}
          />
          <PrivateRoute exact path="/workflow" component={Orders} />
          <PrivateRoute exact path="/workflow/archive" component={Orders} archive variant="archive" />
          <Route
            exact
            path="/workflow/intake"
            render={() => (
              <Redirect to={redirect['/workflow']} />
            )}
          />
          <Route
            exact
            path="/workflow/delivery"
            render={() => (
              <Redirect to={redirect['/workflow']} />
            )}
          />
          <Route
            exact
            path="/workflow/billing"
            render={() => (
              <Redirect to={redirect['/workflow']} />
            )}
          />
          <Route
            exact
            path="/workflow/collection"
            render={() => (
              <Redirect to={redirect['/workflow']} />
            )}
          />
          <Route
            exact
            path="/workflow/archive"
            render={() => (
              <Redirect to={redirect['/workflow']} />
            )}
          />
          <PrivateRoute exact path="/workflow/order/create/patient-assign" component={OrderCreatePatientAssign} />
          <PrivateRoute exact path="/workflow/order/create/patient-create" component={OrderCreatePatientCreate} />
          <PrivateRoute exact path="/workflow/delivery/schedule/day" variant="day" key="delivery-day" component={DeliverySchedule} />
          <PrivateRoute exact path="/workflow/delivery/schedule/week" variant="week" key="delivery-week" component={DeliverySchedule} />
          <PrivateRoute exact path="/workflow/delivery/schedule/month" variant="month" key="delivery-month" component={DeliverySchedule} />
          <PrivateRoute
            exact
            path="/workflow/delivery/schedule/day/print/:id/:dateOfDelivery"
            variant="day"
            key="delivery-day-print"
            component={DeliverySchedulePrint}
          />
          <PrivateRoute
            exact
            path="/workflow/delivery/schedule/week/print/:id/:dateOfDelivery"
            variant="week"
            key="delivery-week-print"
            component={DeliverySchedulePrint}
          />
          <PrivateRoute
            exact
            path="/workflow/delivery/schedule/month/print/:id/:dateOfDelivery"
            variant="month"
            key="delivery-month-print"
            component={DeliverySchedulePrint}
          />
          <Route
            exact
            path="/workflow/delivery/schedule"
            render={route => (
              <Redirect
                to={{
                  pathname: '/workflow/delivery/schedule/day',
                  state: { ...route.location.state },
                }}
              />
            )}
          />
          <PrivateRoute
            exact
            path="/workflow/order/create/patient/:id"
            variant="create"
            form="orderCreate"
            component={OrderCreateEdit}
          />
          <PrivateRoute
            exact
            path="/workflow/order/edit/:id"
            variant="edit"
            form="orderEdit"
            component={OrderCreateEdit}
          />
          <PrivateRoute
            exact
            path="/workflow/archive_order/edit/:id"
            variant="edit"
            form="orderEdit"
            component={OrderCreateEdit}
            archive
          />
          <PrivateRoute
            exact
            path="/workflow/order/copy/:id"
            variant="copy"
            form="orderCopy"
            component={OrderCreateEdit}
          />
          <PrivateRoute exact path="/workflow/order/view/:id" component={OrderView} />
          <PrivateRoute
            exact
            path="/workflow/archive_order/view/:id"
            component={OrderView}
            archive
          />
          <PrivateRoute
            exact
            path="/workflow/order/print/:id/document/:document/:orderLine?"
            component={OrderPrint}
            variant="document"
          />
          <PrivateRoute
            exact
            path="/workflow/order/print/:id/template/:template"
            component={OrderPrint}
            variant="template"
          />
          <PrivateRoute
            exact
            path="/workflow/archive_order/print/:id/document/:document/:orderLine?"
            component={OrderPrint}
            variant="document"
            archive
          />
          <PrivateRoute
            exact
            path="/workflow/archive_order/print/:id/template/:template"
            component={OrderPrint}
            variant="template"
            archive
          />
          <PrivateRoute exact path="/workflow/connected-orders/:id" component={ConnectedOrders} />
          <PrivateRoute exact path="/account/profile" component={Profile} />
          <Route
            exact
            path="/reports"
            render={() => (
              <Redirect to="/reports/custom/create" />
            )}
          />
          <Route
            exact
            path="/reports/custom"
            render={() => (
              <Redirect to="/reports/custom/create" />
            )}
          />
          <PrivateRoute
            exact
            path="/reports/custom/create"
            variant="create"
            form="customReportCreate"
            component={ReportCreateEdit}
          />
          <PrivateRoute
            exact
            path="/reports/custom/:id"
            variant="edit"
            form="customReportEdit"
            component={ReportCreateEdit}
          />
          <Route
            exact
            path="/reports/claim"
            render={() => (
              <Redirect to="/reports/claim/create" />
            )}
          />
          <PrivateRoute
            exact
            path="/reports/claim/create"
            variant="claim"
            form="customReportCreate"
            component={ReportCreateEdit}
          />
          <PrivateRoute
            exact
            path="/reports/claim/:id"
            variant="claim"
            form="customReportEdit"
            component={ReportCreateEdit}
          />
          <Route
            exact
            path="/claims"
            render={() => (
              <Redirect to="/claims/claim-md" />
            )}
          />
          <PrivateRoute
            exact
            path="/claims/claim-md"
            component={Claims}
            variant="claim-md"
          />
          <PrivateRoute
            exact
            path="/claims/change"
            component={Claims}
            variant="change"
          />
          <PrivateRoute
            exact
            path="/reports/insurance"
            variant="insurance"
            form="insuranceReport"
            component={ReportCreateEdit}
          />
          <Route
            exact
            path="/loading"
            render={() => (
              'Loading...'
            )}
          />
          <PrivateRoute exact path="/document/:id" component={DocumentPrint} />
          <PrivateRoute exact path="/versions" component={Versions} />
          <PrivateRoute exact path="/download" component={Download} />
          <PrivateRoute component={Error404} />
        </Switch>
      </div>
    );
  }
}

App.propTypes = {
  account: PropTypes.shape({
    authenticated: PropTypes.bool,
  }).isRequired,
  redirect: PropTypes.shape({
    '/workflow': PropTypes.string.isRequired,
  }).isRequired,
  actions: PropTypes.shape({
    checkAuthentication: PropTypes.func.isRequired,
  }).isRequired,
};

const mapStateToProps = store => ({
  account: store.account,
  redirect: store.routerParams.redirect,
});

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators({ checkAuthentication, setTabID }, dispatch),
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(
  withRoot(App),
));



// WEBPACK FOOTER //
// ./src/App.js